import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';

const GlassyModal = ({ 
  isOpen, 
  onClose, 
  title, 
  icon, 
  children, 
  maxWidth = 'max-w-4xl',
  size = 'default',
  showCloseButton = true,
  fullScreen = false,
  mobileFullScreen = true // New prop for mobile behavior
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        console.log('Escape key pressed, closing modal.');
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    
    // Prevent scrolling on body when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        type: 'spring', 
        damping: 25, 
        stiffness: 300 
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20,
      transition: { 
        duration: 0.2 
      }
    }
  };

  // Determine modal size
  const getModalSize = () => {
    if (maxWidth !== 'max-w-md') return maxWidth; // Use custom maxWidth if provided
    
    switch (size) {
      case 'small': return 'max-w-md';
      case 'large': return 'max-w-4xl';
      case 'extra-large': return 'max-w-6xl';
      case 'full': return 'max-w-7xl';
      default: return 'max-w-2xl';
    }
  };

  // Determine if modal should be full screen
  const shouldBeFullScreen = fullScreen || (isMobile && mobileFullScreen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className={`fixed inset-0 z-[9999] flex items-center justify-center ${shouldBeFullScreen ? 'p-0' : 'p-4 md:p-8'} bg-black/60 backdrop-blur-md`}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={backdropVariants}
          onClick={onClose} // Close modal when clicking backdrop
          style={{ backdropFilter: 'blur(12px)' }}
        >
          {/* Modal Content */}
          <motion.div 
            className={`${shouldBeFullScreen ? 'w-full h-full' : `${getModalSize()} w-full max-h-[90vh] md:max-h-[85vh]`} glass-card ${shouldBeFullScreen ? 'rounded-none' : 'rounded-2xl md:rounded-2xl rounded-t-2xl'} shadow-2xl overflow-hidden flex flex-col relative z-[10000]`}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
            style={{ 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)' 
            }}
          >
            {/* Modal Header */}
            <div className={`relative ${isMobile ? 'p-4' : 'p-6 md:p-8'} border-b border-white/10 flex items-center`}>
              {icon && (
                <div className={`${isMobile ? 'w-8 h-8 mr-3' : 'w-12 h-12 mr-4'} bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center`}>
                  <Icon name={icon} size={isMobile ? 18 : 24} className="text-white" />
                </div>
              )}
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl md:text-2xl'} font-bold text-white flex-1 pr-4`}>{title}</h3>
              
              {showCloseButton && (
                <button 
                  onClick={() => {
                    console.log('Close button clicked, closing modal.');
                    onClose();
                  }}
                  className={`${isMobile ? 'p-2' : 'p-2'} rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors touch-target-44`}
                  aria-label="Close modal"
                >
                  <Icon name="X" size={isMobile ? 20 : 24} />
                </button>
              )}
            </div>
            
            {/* Modal Body */}
            <div className={`${isMobile ? 'p-4' : 'p-6 md:p-8'} text-white/90 flex-grow overflow-y-auto`}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlassyModal;